import { createContext, FC, ReactNode, useCallback, useContext, useState } from "react";

interface BlockRef {
	id: string;
	el: HTMLElement;
}

interface MarginaliaContextValue {
	registerBlock: (id: string, el: HTMLElement | null) => void;
	unregisterBlock: (id: string) => void;
	updateBlockEl: (id: string, el: HTMLElement | null) => void;
	blockRefs: BlockRef[];
	isInline: boolean;
}

const MarginaliaContext = createContext<MarginaliaContextValue>({
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	registerBlock: () => {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	unregisterBlock: () => {},
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	updateBlockEl: () => {},
	blockRefs: [],
	isInline: false,
});

const MarginaliaProvider: FC<{ children: ReactNode; inline?: boolean }> = ({ children, inline = false }) => {
	const [blockRefs, setBlockRefs] = useState<BlockRef[]>([]);

	// The provider survives client-side navigation between detail pages (same route,
	// same element), so a re-registered id must replace its stale, detached element.
	const registerBlock = useCallback((id: string, el: HTMLElement | null) => {
		if (!el) return;
		setBlockRefs((prev) => {
			const existing = prev.find((r) => r.id === id);
			if (existing?.el === el) return prev;
			if (existing) return prev.map((r) => (r.id === id ? { id, el } : r));
			return [...prev, { id, el }];
		});
	}, []);

	const unregisterBlock = useCallback((id: string) => {
		setBlockRefs((prev) => {
			if (!prev.some((r) => r.id === id)) return prev;
			return prev.filter((r) => r.id !== id);
		});
	}, []);

	const updateBlockEl = useCallback((id: string, el: HTMLElement | null) => {
		if (!el) return;
		setBlockRefs((prev) => {
			if (!prev.some((r) => r.id === id)) return prev;
			return prev.map((r) => r.id === id ? { id, el } : r);
		});
	}, []);

	return (
		<MarginaliaContext.Provider value={{ registerBlock, unregisterBlock, updateBlockEl, blockRefs, isInline: inline }}>
			{children}
		</MarginaliaContext.Provider>
	);
};

const useMarginaliaContext = () => useContext(MarginaliaContext);

export { MarginaliaProvider, useMarginaliaContext };
